/**
 * API client for conversation endpoints
 */

import { apiClient } from './client';

export interface Message {
  id: string;
  conversation_id: string;
  channel: string;
  direction: string;
  content: string;
  sent_by: string;
  sent_at: string;
  order_id: string | null;
  attachments: Record<string, any> | null;
  metadata_: Record<string, any> | null;
}

export interface Conversation {
  id: string;
  client_id: string;
  client_name: string | null;
  channel: string;
  channel_user_id: string;
  order_id: string | null;
  status: string;
  created_at: string;
  last_message_at: string | null;
  bot_enabled?: boolean;
  metadata_?: Record<string, any> | null;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
}

export interface ConversationListParams {
  page?: number;
  per_page?: number;
  channel?: string;
  status?: string;
  client_id?: string;
  order_id?: string;
  search?: string;
}

export interface SendMessageRequest {
  content: string;
  attachments?: Record<string, any>;
  override_bot?: boolean;
}

export interface UpdateSettingsRequest {
  bot_enabled?: boolean;
  assigned_admin_id?: string;
}

export const conversationsApi = {
  /**
   * Get paginated list of conversations
   */
  async getList(params: ConversationListParams = {}) {
    const response = await apiClient.get('/admin/conversations/', { params });
    return response.data;
  },

  /**
   * Get conversation detail with messages
   */
  async getDetail(conversationId: string): Promise<ConversationDetail> {
    const response = await apiClient.get(
      `/admin/conversations/${conversationId}`
    );
    return response.data;
  },

  /**
   * Send admin message to client
   */
  async sendMessage(
    conversationId: string,
    data: SendMessageRequest
  ): Promise<Message> {
    const response = await apiClient.post(
      `/admin/conversations/${conversationId}/send-message`,
      data
    );
    return response.data;
  },

  /**
   * Update conversation settings
   */
  async updateSettings(
    conversationId: string,
    data: UpdateSettingsRequest
  ): Promise<{ status: string; conversation_id: string }> {
    const response = await apiClient.patch(
      `/admin/conversations/${conversationId}/settings`,
      data
    );
    return response.data;
  },

  /**
   * Toggle bot for conversation
   */
  async toggleBot(
    conversationId: string,
    enabled: boolean
  ): Promise<{ status: string }> {
    return this.updateSettings(conversationId, { bot_enabled: enabled });
  },
};
